/**
 * Settings Store
 *
 * Wraps the existing settings.ts localStorage functions with Zustand
 * for reactive state management and server sync capability.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  getSettings,
  saveSettings,
  getEnabledTypes,
  getEnabledSubtypes,
  getRandomEnabledType,
  getRandomEnabledSubtype,
  type QuestionSettings,
  type CircuitSettings,
} from '@/lib/settings';

interface SettingsState {
  // State
  settings: QuestionSettings;
  isLoaded: boolean;
  isSyncing: boolean;
  lastSyncedAt: number | null;

  // Actions
  loadSettings: () => void;
  updateSettings: (settings: QuestionSettings) => void;
  updateCircuitSettings: (type: keyof CircuitSettings, enabled: boolean, subtypes?: Record<string, boolean>) => void;
  syncToServer: (sessionId: string) => Promise<void>;

  // Helpers (delegate to settings.ts)
  getEnabledTypes: (category: 'circuits') => string[];
  getEnabledSubtypes: (category: 'circuits', type: string) => string[];
  getRandomType: (category: 'circuits') => string;
  getRandomSubtype: (category: 'circuits', type: string) => string;
}

export const useSettingsStore = create<SettingsState>()(
  devtools(
    (set, get) => ({
      // Initial state - load from localStorage
      settings: getSettings(),
      isLoaded: true,
      isSyncing: false,
      lastSyncedAt: null,

      // Load settings from localStorage
      loadSettings: () => {
        const settings = getSettings();
        set({ settings, isLoaded: true });
      },

      // Update settings (saves to localStorage immediately)
      updateSettings: (settings) => {
        saveSettings(settings);
        set({ settings });
      },

      // Update a specific circuit setting
      updateCircuitSettings: (type, enabled, subtypes) => {
        const { settings } = get();
        const newSettings: QuestionSettings = {
          ...settings,
          circuits: {
            ...settings.circuits,
            [type]: {
              enabled,
              subtypes: subtypes || settings.circuits[type].subtypes,
            },
          },
        };
        saveSettings(newSettings);
        set({ settings: newSettings });
      },

      // Sync settings to server via REST API
      syncToServer: async (sessionId) => {
        if (!sessionId) return;

        const { settings } = get();
        set({ isSyncing: true });

        try {
          const response = await fetch(`/api/sessions/${sessionId}/settings`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-ID': sessionId,
            },
            body: JSON.stringify({ settings }),
          });

          if (response.ok) {
            set({ lastSyncedAt: Date.now() });
          }
        } catch (error) {
          console.error('Failed to sync settings:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      // Helper methods that delegate to settings.ts functions
      getEnabledTypes: (category) => getEnabledTypes(category),
      getEnabledSubtypes: (category, type) => getEnabledSubtypes(category, type),
      getRandomType: (category) => getRandomEnabledType(category),
      getRandomSubtype: (category, type) => getRandomEnabledSubtype(category, type),
    }),
    { name: 'settings-store' }
  )
);

// Selectors for optimized subscriptions
export const selectSettings = (state: SettingsState) => state.settings;
export const selectCircuitSettings = (state: SettingsState) => state.settings.circuits;
export const selectIsLoaded = (state: SettingsState) => state.isLoaded;
export const selectIsSyncing = (state: SettingsState) => state.isSyncing;
